import type { ContentLang } from '../constants/bilingual.constants.js';

/**
 * Content has two languages. Every Chinese variant a browser can report
 * (zh, zh-CN, zh-TW, zh-HK, zh-Hant, ...) reads the Chinese side; everything
 * else reads English (PRD §5.1).
 */
export function isZhLocale(locale: string | null | undefined): boolean {
  // "zh_TW" (underscore, as some platforms report it) counts too.
  return typeof locale === 'string' && /^zh(?:$|[-_])/i.test(locale);
}

/** The three site languages: English plus Chinese in two scripts (PRD §5.1). */
export const SITE_LANGUAGES = ['en', 'zh-CN', 'zh-TW'] as const;
export type SiteLanguageCode = (typeof SITE_LANGUAGES)[number];

/**
 * Any language tag to a site language. Traditional for an explicit Hant
 * script or a Taiwan, Hong Kong or Macau region; other Chinese is Simplified;
 * everything else is English.
 */
export function normalizeSiteLanguage(tag: string | null | undefined): SiteLanguageCode {
  if (!isZhLocale(tag)) return 'en';
  const t = String(tag);
  if (/[-_]hans\b/i.test(t)) return 'zh-CN';
  if (/[-_]hant\b/i.test(t) || /[-_](tw|hk|mo)\b/i.test(t)) return 'zh-TW';
  return 'zh-CN';
}

export function isTraditionalLocale(locale: string | null | undefined): boolean {
  return normalizeSiteLanguage(locale) === 'zh-TW';
}

/** Site language codes as stored on the account (User.preferredLanguage). */
export const SITE_LANGUAGE_TO_DB = { en: 'EN', 'zh-CN': 'ZH_CN', 'zh-TW': 'ZH_TW' } as const;
export type SiteLanguageDb = (typeof SITE_LANGUAGE_TO_DB)[SiteLanguageCode];
export const SITE_LANGUAGE_FROM_DB: Record<SiteLanguageDb, SiteLanguageCode> = {
  EN: 'en',
  ZH_CN: 'zh-CN',
  ZH_TW: 'zh-TW',
};

export function isSiteLanguageCode(value: unknown): value is SiteLanguageCode {
  return typeof value === 'string' && (SITE_LANGUAGES as readonly string[]).includes(value);
}

/** Site locale as a ContentLang. */
export function localeToContentLang(locale: string | null | undefined): ContentLang {
  return isZhLocale(locale) ? 'ZH' : 'EN';
}

export function otherLang(lang: ContentLang): ContentLang {
  return lang === 'ZH' ? 'EN' : 'ZH';
}

/**
 * The one rule for showing a bilingual pair (PRD §5.1): the current language's
 * side, else the other side, else empty. Never returns whitespace-only text.
 */
export function pickLocalized(
  zh: string | null | undefined,
  en: string | null | undefined,
  locale: string | null | undefined
): string {
  const z = zh?.trim() ?? '';
  const e = en?.trim() ?? '';
  return isZhLocale(locale) ? z || e : e || z;
}

/**
 * Non-page readers (emails, Stripe, CSV, snapshots) keep their existing
 * language preference and only gain the fallback (PRD §5.3).
 */
export function zhFirst(zh: string | null | undefined, en: string | null | undefined): string {
  return zh?.trim() || en?.trim() || '';
}

export function enFirst(zh: string | null | undefined, en: string | null | undefined): string {
  return en?.trim() || zh?.trim() || '';
}

/** Normalize one side for storage: trim, and treat empty as absent. */
export function normalizeSide(value: string | null | undefined): string | null {
  if (typeof value !== 'string') return null;
  const t = value.trim();
  return t === '' ? null : t;
}

const CJK = /[\u3400-\u4dbf\u4e00-\u9fff\uf900-\ufaff\u3000-\u303f\uff00-\uffef]/;
const LATIN = /[A-Za-z]/;

/**
 * Cheap plausibility check that `text` can be in `lang`: Chinese must contain
 * at least one CJK character, English at least one Latin letter (PRD §6.3).
 */
export function looksLikeLang(text: string, lang: ContentLang): boolean {
  return lang === 'ZH' ? CJK.test(text) : LATIN.test(text);
}
